import * as React from "react"

import { cva, type VariantProps } from "class-variance-authority"
import { Pencil } from "lucide-react"

import { cn } from "../../cn"
import { Input } from "../Input/Input"
import { Loader } from "../Loader/Loader"
import { getUserFacingErrorMessage } from "../Toast/userFacingErrors"
import { TruncatedText } from "../TruncatedText/TruncatedText"

const SAVE_FAILED_MESSAGE = "Couldn't save the name. Try again."

/**
 * A save rejection whose message is already written for the user, such as a duplicate-name reason. EditableTitle shows its message verbatim; any other rejection is mapped to a generic message.
 */
export class EditableTitleSaveError extends Error {
  constructor(message: string) {
    super(message)
    this.name = "EditableTitleSaveError"
  }
}

// The text and the field share one height per size, so opening the field does not move anything around it.
const editableTitleTextVariants = cva("text-coval-text", {
  variants: {
    size: {
      sm: "h-6 text-sm font-semibold",
      md: "h-7 text-base font-semibold",
      lg: "h-8 text-xl font-semibold",
    },
  },
  defaultVariants: {
    size: "md",
  },
})

// Input's h-control is emitted after the numeric heights, so the field needs the important form to match the text.
const FIELD_HEIGHT_CLASS = {
  sm: "h-6!",
  md: "h-7!",
  lg: "h-8!",
} as const

export interface EditableTitleProps extends VariantProps<
  typeof editableTitleTextVariants
> {
  /** The saved title. The field starts from this value each time it opens. */
  value: string
  /**
   * Called with the trimmed title when it is non-empty, changed, and valid. A
   * returned promise holds the field in its saving state; a rejection keeps
   * the field open with the error. Reject with `EditableTitleSaveError` to
   * show a message as written.
   */
  onSave: (next: string) => void | Promise<void>
  /** Accessible name of the field, e.g. "Report name". */
  label: string
  /** Shown in place of an empty title and inside an empty field. */
  placeholder?: string
  /** Longest title accepted, in Unicode code points. Pass the resource's backend limit. */
  maxLength?: number
  /** Opens the field with the title selected on mount, for a resource the user has just created and names first. Has no effect when `editing` controls the field. */
  focusOnMount?: boolean
  /** Returns an error message for a title that cannot be saved. */
  validate?: (next: string) => string | undefined
  /** An error owned by the caller, shown under the title. */
  error?: React.ReactNode
  /** Marks a save the caller is running itself. */
  saving?: boolean
  /** Shows the title as plain text with no way to edit it. */
  disabled?: boolean
  /**
   * Whether the field is open, for a caller that opens it from elsewhere, such as a row menu's Rename item. Pair it with `onEditingChange`; leave both unset to let a click on the title open the field.
   */
  editing?: boolean
  /** Called when the field opens or closes. */
  onEditingChange?: (editing: boolean) => void
  /**
   * `"click"` makes the title itself open the field. `"external"` shows the title as plain text, so a click falls through to what contains it, such as a table row that navigates, and only `editing` opens the field.
   */
  trigger?: "click" | "external"
  className?: string
}

// Counts Unicode code points, as the backend does, so an emoji is one character rather than two UTF-16 units.
function countCharacters(text: string) {
  return Array.from(text).length
}

function isPromiseLike(value: unknown): value is PromiseLike<void> {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as PromiseLike<void>).then === "function"
  )
}

/**
 * A title that becomes a field where it sits. Clicking the text opens the
 * field with the title selected; Enter or blur saves, Escape restores the
 * saved title. An empty or unchanged title saves nothing. A title clipped by
 * its container shows in full in a tooltip on hover.
 */
const EditableTitle = React.forwardRef<HTMLDivElement, EditableTitleProps>(
  (
    {
      value,
      onSave,
      label,
      placeholder = "Untitled",
      maxLength,
      focusOnMount = false,
      validate,
      error,
      saving = false,
      disabled = false,
      editing,
      onEditingChange,
      trigger = "click",
      size,
      className,
    },
    ref,
  ) => {
    // focusOnMount seeds only the first render, so a title that turns it on later is not pulled into editing mid-page.
    const [uncontrolledEditing, setUncontrolledEditing] = React.useState(
      focusOnMount && !disabled,
    )
    const isControlled = editing !== undefined
    const isEditing = isControlled ? editing : uncontrolledEditing
    const [preparedEditing, setPreparedEditing] = React.useState(isEditing)
    const [draft, setDraft] = React.useState(value)
    const [internalError, setInternalError] = React.useState<string>()
    const [isPending, setIsPending] = React.useState(false)
    const inputRef = React.useRef<HTMLInputElement | null>(null)
    const buttonRef = React.useRef<HTMLButtonElement | null>(null)
    const readOnlyTitleRef = React.useRef<HTMLSpanElement | null>(null)
    // Set when Enter, Escape, disabling, or a swap to another title closes the field, so focus returns to the title instead of falling to the page body.
    const restoreFocusRef = React.useRef(false)
    // Enter and Escape both close the field, and the blur that follows would
    // otherwise save a second time or save what Escape just discarded.
    const isFinishedRef = React.useRef(false)
    // Each opened edit gets a generation; a pending save only settles the field while its generation is still current, so a cancel, disable, unmount, or swap to another title drops its result.
    const editGenerationRef = React.useRef(0)
    // The trimmed title a pending save is writing, so the caller echoing it back through `value` is not mistaken for a different title.
    const savingValueRef = React.useRef<string | undefined>(undefined)
    // The trimmed title whose save last failed. Blur does not resend it, so a retry of a rejected save takes an explicit Enter.
    const failedValueRef = React.useRef<string | undefined>(undefined)
    // The title the open edit started from, so a live update to `value` mid-edit does not turn an untouched draft into a save of the older title.
    const editStartValueRef = React.useRef(value)
    const errorId = React.useId()
    const valueRef = React.useRef(value)
    const isControlledRef = React.useRef(isControlled)
    const onEditingChangeRef = React.useRef(onEditingChange)
    React.useLayoutEffect(() => {
      valueRef.current = value
      isControlledRef.current = isControlled
      onEditingChangeRef.current = onEditingChange
    })

    const setIsEditing = React.useCallback((next: boolean) => {
      if (!isControlledRef.current) {
        setUncontrolledEditing(next)
      }
      onEditingChangeRef.current?.(next)
    }, [])

    // Opening the field, whether from a click or from the caller, starts the draft from the saved title.
    if (isEditing !== preparedEditing) {
      setPreparedEditing(isEditing)
      if (isEditing) {
        setDraft(value)
        setInternalError(undefined)
      }
    }

    const isBusy = saving || isPending
    const shownError = internalError ?? error

    React.useEffect(
      () => () => {
        editGenerationRef.current += 1
      },
      [],
    )

    // Closing in an effect rather than during render lets the field first re-render as disabled, so the blur from unmounting it runs a handler that sees `disabled` and saves nothing.
    React.useEffect(() => {
      if (disabled && isEditing) {
        editGenerationRef.current += 1
        if (inputRef.current) {
          restoreFocusRef.current = true
        }
        setIsEditing(false)
        setIsPending(false)
        setInternalError(undefined)
      }
    }, [disabled, isEditing, setIsEditing])

    // A `value` that is neither the title the edit started from nor the one being saved means the caller now shows a different title, so the pending save no longer belongs to this field.
    React.useEffect(() => {
      if (
        isPending &&
        value !== editStartValueRef.current &&
        value !== savingValueRef.current
      ) {
        editGenerationRef.current += 1
        restoreFocusRef.current = true
        setIsEditing(false)
        setIsPending(false)
        setInternalError(undefined)
      }
    }, [isPending, setIsEditing, value])

    React.useEffect(() => {
      if (isEditing) {
        isFinishedRef.current = false
        restoreFocusRef.current = false
        failedValueRef.current = undefined
        editGenerationRef.current += 1
        editStartValueRef.current = valueRef.current
        inputRef.current?.focus()
        inputRef.current?.select()
        return
      }
      if (!restoreFocusRef.current) {
        return
      }
      restoreFocusRef.current = false
      // A save that settles after the user has moved focus elsewhere leaves focus where they put it.
      if (
        document.activeElement === null ||
        document.activeElement === document.body
      ) {
        const title = buttonRef.current ?? readOnlyTitleRef.current
        title?.focus()
      }
    }, [isEditing])

    const startEditing = () => {
      if (disabled || isBusy) {
        return
      }
      setIsEditing(true)
    }

    const cancel = () => {
      isFinishedRef.current = true
      editGenerationRef.current += 1
      setInternalError(undefined)
      setIsEditing(false)
    }

    const fail = (next: string, reason: unknown) => {
      isFinishedRef.current = false
      failedValueRef.current = next
      setInternalError(
        reason instanceof EditableTitleSaveError
          ? reason.message
          : getUserFacingErrorMessage(reason, {
              fallback: SAVE_FAILED_MESSAGE,
            }),
      )
    }

    const getLengthError = (next: string) =>
      maxLength !== undefined && countCharacters(next) > maxLength
        ? `Use ${maxLength} characters or fewer.`
        : undefined

    const getValidationError = (next: string) =>
      getLengthError(next) ?? validate?.(next)

    const commit = async () => {
      if (isFinishedRef.current || isBusy || disabled) {
        return
      }
      const next = draft.trim()
      if (!next || next === editStartValueRef.current.trim()) {
        cancel()
        return
      }
      const validationError = getValidationError(next)
      if (validationError) {
        setInternalError(validationError)
        return
      }

      // A new attempt supersedes the message from a rejected save or an earlier validation failure.
      setInternalError(undefined)
      isFinishedRef.current = true
      let result: void | Promise<void>
      try {
        result = onSave(next)
      } catch (reason) {
        fail(next, reason)
        return
      }
      if (!isPromiseLike(result)) {
        setIsEditing(false)
        return
      }

      const generation = editGenerationRef.current
      const isCurrent = () => generation === editGenerationRef.current
      savingValueRef.current = next
      setIsPending(true)
      try {
        await result
        if (isCurrent()) {
          setIsEditing(false)
        }
      } catch (reason) {
        if (isCurrent()) {
          fail(next, reason)
        }
      } finally {
        if (isCurrent()) {
          setIsPending(false)
        }
      }
    }

    const textClassName = editableTitleTextVariants({ size })
    const isEmpty = value.length === 0

    let title: React.ReactNode
    if (isEditing) {
      // An input has no width of its own in a content-sized parent such as a breadcrumb, so a hidden copy of the draft sits in the same grid cell and sizes it.
      title = (
        <span className="inline-grid min-w-0 max-w-full">
          <span
            aria-hidden="true"
            className={cn(
              textClassName,
              "invisible col-start-1 row-start-1 overflow-hidden whitespace-pre border border-transparent px-1",
            )}
          >
            {`${draft || placeholder} `}
          </span>
          <Input
            ref={inputRef}
            aria-label={label}
            aria-invalid={shownError ? true : undefined}
            aria-describedby={shownError ? errorId : undefined}
            aria-busy={isBusy || undefined}
            value={draft}
            placeholder={placeholder}
            readOnly={isBusy}
            disabled={disabled}
            error={Boolean(shownError)}
            onChange={(event) => {
              setDraft(event.target.value)
              // The native maxLength attribute counts UTF-16 units and would cut an emoji or an IME composition short, so the limit is reported here instead.
              setInternalError(getLengthError(event.target.value.trim()))
            }}
            onBlur={() => {
              if (!isFinishedRef.current && !disabled) {
                restoreFocusRef.current = false
              }
              if (draft.trim() === failedValueRef.current) {
                return
              }
              void commit()
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.nativeEvent.isComposing) {
                event.preventDefault()
                event.stopPropagation()
                restoreFocusRef.current = true
                void commit()
              }
              if (
                event.key === "Escape" &&
                !isBusy &&
                !event.nativeEvent.isComposing
              ) {
                event.preventDefault()
                event.stopPropagation()
                restoreFocusRef.current = true
                cancel()
              }
            }}
            className={cn(
              textClassName,
              FIELD_HEIGHT_CLASS[size ?? "md"],
              "col-start-1 row-start-1 min-w-0 px-1 py-0",
            )}
          />
        </span>
      )
    } else if (disabled || trigger === "external") {
      title = (
        <span
          ref={readOnlyTitleRef}
          tabIndex={-1}
          className={cn(
            textClassName,
            "inline-flex min-w-0 items-center border border-transparent px-1 outline-none",
            isEmpty && "text-coval-text-muted",
          )}
        >
          <TruncatedText>{isEmpty ? placeholder : value}</TruncatedText>
        </span>
      )
    } else {
      title = (
        <button
          ref={buttonRef}
          type="button"
          onClick={startEditing}
          disabled={isBusy}
          aria-describedby={shownError ? errorId : undefined}
          className={cn(
            textClassName,
            "group/editable-title inline-flex min-w-0 cursor-text items-center gap-1.5 rounded-md border border-transparent px-1 text-left",
            "transition-colors duration-150 hover:bg-surface-1",
            "disabled:cursor-default disabled:hover:bg-transparent",
            isEmpty && "text-coval-text-muted",
          )}
        >
          <span className="sr-only">Edit {label}: </span>
          <TruncatedText>{isEmpty ? placeholder : value}</TruncatedText>
          {!isBusy && (
            <Pencil
              aria-hidden="true"
              className="size-3.5 shrink-0 text-coval-text-muted opacity-0 transition-opacity group-hover/editable-title:opacity-100 group-focus-visible/editable-title:opacity-100"
            />
          )}
        </button>
      )
    }

    return (
      <div ref={ref} className={cn("flex min-w-0 flex-col gap-1", className)}>
        <div className="flex min-w-0 items-center gap-1.5">
          {title}
          {isBusy && <Loader size="sm" label="Saving" />}
        </div>
        {shownError && (
          <p
            id={errorId}
            role="alert"
            className="text-xs text-coval-danger-text"
          >
            {shownError}
          </p>
        )}
      </div>
    )
  },
)

EditableTitle.displayName = "EditableTitle"

export { EditableTitle }
